import {createSlice, isAnyOf} from '@reduxjs/toolkit';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import {formatDateAsKey} from '@lib/utils';
import {
  connectCalendar,
  loadMeetingPreferences,
  loadMeetings,
  selectMeetingAttendeeGroupItem,
  selectMeeting,
  setLastRequestedDate,
  unselectMeeting,
  updateUserClosedConsentTilePreference,
  clearLastRequestedDate,
  refreshMeetingPreferences,
  clearAllMeetings,
  disconnectCalendar,
  loadMeetingTimeSlotSuggestions,
  createMeeting,
  searchInvitees,
  searchMultipleInvitees,
  deleteMeeting,
  setUserCalendarConsentAsInvalid,
  loadTimeZones,
  loadAvailabilityOfInviteesSuggestions,
  enableCalendarAfterRedirection,
  redirectForCalendarConsent,
  patchMeetingUserPreferences,
  updateMeeting,
  getConsentUrlForCalendar,
} from './actions';
import {meetingsAdapter, suggestionsAdapter, timeZonesAdapter} from './adapters';
import {getInitialState} from './initialState';
import parseISO from 'date-fns/parseISO';

const initialState = getInitialState();

const slice = createSlice({
  name: 'meetings',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadMeetingPreferences.pending, (state) => {
        state.meetingPreferencesLoadingStatus = 'Loading';
      })
      .addCase(loadMeetingPreferences.fulfilled, (state, action) => {
        const {settings, userPreferences} = action.payload;
        state.settings = settings;
        state.userPreferences = userPreferences;
        state.meetingPreferencesLoadingStatus = 'Loaded';
      })
      .addCase(loadMeetingPreferences.rejected, (state) => {
        state.meetingPreferencesLoadingStatus = 'Failed';
      })
      .addCase(refreshMeetingPreferences.fulfilled, (state, action) => {
        const {settings, userPreferences} = action.payload;
        state.settings = settings;
        state.userPreferences = userPreferences;
      })
      .addCase(loadMeetings.pending, (state, action) => {
        const {from, to} = action.meta.arg;
        eachDayOfInterval({start: parseISO(from), end: parseISO(to)})
          .map(formatDateAsKey)
          .forEach((dateString) => {
            state.meetingsLoadingStatus[dateString] = 'Loading';
          });
      })
      .addCase(loadMeetings.fulfilled, (state, action) => {
        const {from, to} = action.meta.arg;
        eachDayOfInterval({start: parseISO(from), end: parseISO(to)})
          .map(formatDateAsKey)
          .forEach((dateString) => {
            state.meetingsLoadingStatus[dateString] = 'Loaded';
          });
      })
      .addCase(loadMeetings.rejected, (state, action) => {
        const {from, to} = action.meta.arg;
        eachDayOfInterval({start: parseISO(from), end: parseISO(to)})
          .map(formatDateAsKey)
          .forEach((dateString) => {
            state.meetingsLoadingStatus[dateString] = 'Failed';
          });
      })
      .addCase(loadMeetingTimeSlotSuggestions.fulfilled, (state, action) => {
        suggestionsAdapter.setAll(state.suggestions, action.payload.suggestions ?? {});
        state.meetingSuggestionLoadingStatus = 'Loaded';
      })
      .addCase(loadMeetingTimeSlotSuggestions.pending, (state) => {
        state.meetingSuggestionLoadingStatus = 'Loading';
      })
      .addCase(loadMeetingTimeSlotSuggestions.rejected, (state) => {
        state.suggestions = initialState.suggestions;
        state.meetingSuggestionLoadingStatus = 'Failed';
      })
      .addCase(searchInvitees.pending, (state) => {
        state.inviteeSearchLoadingStatus = 'Loading';
      })
      .addCase(searchInvitees.fulfilled, (state, action) => {
        state.meetingInviteeSearchResults = action.payload;
        state.inviteeSearchLoadingStatus = 'Loaded';
      })
      .addCase(searchInvitees.rejected, (state) => {
        state.inviteeSearchLoadingStatus = 'Failed';
      })
      .addCase(searchMultipleInvitees.pending, (state) => {
        state.inviteeSearchLoadingStatus = 'Loading';
      })
      .addCase(searchMultipleInvitees.fulfilled, (state, action) => {
        state.meetingInviteeSearchResults = action.payload;
        state.inviteeSearchLoadingStatus = 'Loaded';
      })
      .addCase(searchMultipleInvitees.rejected, (state) => {
        state.inviteeSearchLoadingStatus = 'Failed';
      })
      .addCase(createMeeting.fulfilled, (state) => {
        state.meetingCreationStatus = 'Loaded';
      })
      .addCase(createMeeting.pending, (state) => {
        state.meetingCreationStatus = 'Loading';
      })
      .addCase(createMeeting.rejected, (state) => {
        state.meetingCreationStatus = 'Failed';
      })
      .addCase(selectMeeting, (state, action) => {
        state.selectedMeetingId = action.payload;
      })
      .addCase(unselectMeeting, (state) => {
        state.selectedMeetingId = '';
      })
      .addCase(selectMeetingAttendeeGroupItem, (state, action) => {
        state.selectedMeetingAttendeeGroupId = action.payload;
      })
      .addCase(setUserCalendarConsentAsInvalid, (state, action) => {
        state.isUserCalendarConsentInvalid = action.payload;
      })
      .addCase(connectCalendar.pending, (state) => {
        state.isUpdatingCalendarConsent = true;
      })
      .addCase(connectCalendar.fulfilled, (state, action) => {
        const isValid = action.payload;

        state.userPreferences.enableCalendar = isValid;
        state.isUpdatingCalendarConsent = false;
      })
      .addCase(connectCalendar.rejected, (state) => {
        state.isUpdatingCalendarConsent = false;
      })
      .addCase(redirectForCalendarConsent.pending, (state) => {
        state.isUpdatingCalendarConsent = true;
      })
      .addCase(redirectForCalendarConsent.fulfilled, (state) => {
        state.isUpdatingCalendarConsent = true;
      })
      .addCase(redirectForCalendarConsent.rejected, (state) => {
        state.isUpdatingCalendarConsent = false;
      })
      .addCase(getConsentUrlForCalendar.pending, (state) => {
        state.isUpdatingCalendarConsent = true;
      })
      .addCase(getConsentUrlForCalendar.fulfilled, (state) => {
        state.isUpdatingCalendarConsent = true;
      })
      .addCase(getConsentUrlForCalendar.rejected, (state) => {
        state.isUpdatingCalendarConsent = false;
      })
      .addCase(enableCalendarAfterRedirection.pending, (state) => {
        state.isUpdatingCalendarConsent = true;
      })
      .addCase(enableCalendarAfterRedirection.fulfilled, (state, action) => {
        const isValid = action.payload;

        state.userPreferences.enableCalendar = isValid;
        state.isUpdatingCalendarConsent = false;
      })
      .addCase(enableCalendarAfterRedirection.rejected, (state) => {
        state.isUpdatingCalendarConsent = false;
      })
      .addCase(disconnectCalendar.pending, (state) => {
        state.isUpdatingCalendarConsent = true;
      })
      .addCase(disconnectCalendar.fulfilled, (state) => {
        state.isUpdatingCalendarConsent = false;
        state.userPreferences.enableCalendar = false;
      })
      .addCase(disconnectCalendar.rejected, (state) => {
        state.isUpdatingCalendarConsent = false;
      })
      .addCase(setLastRequestedDate, (state, action) => {
        state.lastRequestedISODate = action.payload;
      })
      .addCase(updateUserClosedConsentTilePreference.fulfilled, (state, action) => {
        state.userPreferences.userClosedConsentTile = action.payload;
      })
      .addCase(clearLastRequestedDate, (state) => {
        state.lastRequestedISODate = initialState.lastRequestedISODate;
      })
      .addCase(clearAllMeetings, (state) => {
        meetingsAdapter.removeAll(state.items);
      })
      .addCase(deleteMeeting.pending, (state) => {
        state.isDeleting = true;
      })
      .addCase(deleteMeeting.fulfilled, (state, action) => {
        const meeting = action.payload;
        meetingsAdapter.removeOne(state.items, meeting.id);
      })
      .addCase(loadTimeZones.fulfilled, (state, action) => {
        timeZonesAdapter.addMany(state.timeZones, action.payload.timeZones);
        state.areTimeZonesLoading = false;
      })
      .addCase(loadTimeZones.pending, (state) => {
        state.areTimeZonesLoading = true;
      })
      .addCase(loadTimeZones.rejected, (state) => {
        state.areTimeZonesLoading = false;
      })
      .addCase(loadAvailabilityOfInviteesSuggestions.fulfilled, (state, {payload}) => {
        state.attendeeAvailabilityLoadingStatus = 'Loaded';
        state.currentAttendeeAvailabilities = payload;
      })
      .addCase(loadAvailabilityOfInviteesSuggestions.rejected, (state) => {
        state.attendeeAvailabilityLoadingStatus = 'Failed';
      })
      .addCase(loadAvailabilityOfInviteesSuggestions.pending, (state) => {
        state.attendeeAvailabilityLoadingStatus = 'Loading';
      })
      .addCase(patchMeetingUserPreferences.fulfilled, (state, {payload}) => {
        state.userPreferences = payload;
        state.isUpdatingMeetingUserPreferences = false;
      })
      .addCase(patchMeetingUserPreferences.rejected, (state) => {
        state.isUpdatingMeetingUserPreferences = false;
      })
      .addCase(patchMeetingUserPreferences.pending, (state) => {
        state.isUpdatingMeetingUserPreferences = true;
      })
      .addCase(updateMeeting.pending, (state) => {
        state.meetingUpdateLoadingStatus = 'Loading';
      })
      .addCase(updateMeeting.fulfilled, (state) => {
        state.meetingUpdateLoadingStatus = 'Loaded';
      })
      .addCase(updateMeeting.rejected, (state) => {
        state.meetingUpdateLoadingStatus = 'Failed';
      })
      .addMatcher(
        isAnyOf(loadMeetings.fulfilled, createMeeting.fulfilled, updateMeeting.fulfilled),
        (state, action) => {
          const {meetings} = action.payload;
          meetingsAdapter.upsertMany(state.items, meetings ?? {});
        },
      )
      .addMatcher(isAnyOf(deleteMeeting.fulfilled, deleteMeeting.rejected), (state) => {
        state.isDeleting = false;
      });
  },
});

export const {reducer: meetingsReducer} = slice;
